// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  AuditEventContext,
  AuditEventContextAction,
  AuditSourceType,
} from '@/audit/api/types';
import { SHIPMENT_AUDIT_OPERATIONS } from '@/constants/shipment-audit-operation';
import { getEffectiveLocationName } from '@/location/utilities/get-effective-third-party';
import { Tooltip, Typography } from '@mui/material';
import { Fragment, ReactNode } from 'react';

const ACTION_LABELS: Record<AuditEventContextAction, string> = {
  CREATE: 'created',
  UPDATE: 'updated',
  DELETE: 'deleted',
  LOGIN: 'logged in',
  LOGOUT: 'logged out',
  MERGE: 'merged',
};

const SOURCE_LABELS: Record<AuditSourceType, string> = {
  UPLOAD: 'file upload',
  API_KEY: 'API key',
  CARRIER_UPDATE: 'carrier update',
  HUBSPOT_API: 'HubSpot API',
  SFDC_API: 'Salesforce API',
  SFDC_SYNC: 'Salesforce sync',
  THIRD_PARTY_API: 'Third party API',
  INHERIT: 'inherit',
  NETSUITE: 'NetSuite API',
  QBO: 'QuickBooks API',
  OAUTH: 'connected application',
};

const SOURCE_NOUN_LABELS: Record<AuditSourceType, string> = {
  UPLOAD: 'file upload',
  API_KEY: 'API',
  CARRIER_UPDATE: 'carrier update',
  HUBSPOT_API: 'HubSpot',
  SFDC_API: 'Salesforce',
  SFDC_SYNC: 'Salesforce',
  THIRD_PARTY_API: 'Third party',
  INHERIT: 'inherit',
  NETSUITE: 'NetSuite',
  QBO: 'QuickBooks',
  OAUTH: 'Connected application',
};

function getActionLabelForAssetEvent(action: AuditEventContextAction) {
  if (action === 'CREATE') {
    return 'logged';
  }

  return ACTION_LABELS[action];
}

function getActionLabelForFile(action: AuditEventContextAction) {
  if (action === 'CREATE') {
    return 'uploaded';
  }

  return ACTION_LABELS[action];
}

function getActionLabelForURLLink(action: AuditEventContextAction) {
  if (action === 'CREATE') {
    return 'added';
  }

  return ACTION_LABELS[action];
}

/**
 * Every shipment operation other than create reads as an update — the entry's
 * status property is what names the transition, so the verb does not repeat it.
 */
function getShipmentOperationLabel(
  operation: string | undefined,
  action: AuditEventContextAction
) {
  if (operation === SHIPMENT_AUDIT_OPERATIONS.CREATE) {
    return 'created';
  }

  return operation === undefined ? ACTION_LABELS[action] : 'updated';
}

// Reads the single file/link property to phrase the event as an add or a
// remove, falling back to a generic change.
function getFileOrLinkChangeVerb(
  properties: readonly {
    type: string;
    oldValue?: unknown;
    newValue?: unknown;
  }[],
  propertyType: 'FILE' | 'URL_LINK',
  noun: 'file' | 'link'
): string {
  const property = properties.find((entry) => entry.type === propertyType);
  const added =
    property != null && property.newValue != null && property.oldValue == null;
  const removed =
    property != null && property.oldValue != null && property.newValue == null;
  if (added) {
    return `added a ${noun} to`;
  }
  if (removed) {
    return `removed a ${noun} from`;
  }
  return `changed the ${noun}s on`;
}

function getFulfillmentOrderTriggerLabel(
  eventIdentifier: string,
  action: AuditEventContextAction,
  properties: readonly {
    type: string;
    oldValue?: unknown;
    newValue?: unknown;
  }[],
  isThirdParty: boolean
) {
  switch (eventIdentifier) {
    case 'FULFILLMENT_ORDER_CREATE':
      return 'created';
    case 'FULFILLMENT_ORDER_CANCEL':
      return 'cancelled';
    case 'FULFILLMENT_ORDER_CLOSE':
      // A user's manual close reads as "closed"; Hardfin's own reconciliation of
      // a 3PL FIN close reads as "updated" (its source is dropped, not shown as a
      // "via").
      return isThirdParty ? 'updated' : 'closed';
    case 'FULFILLMENT_ORDER_SEND_TO_3PL':
      // The user's send reads as "updated"; the system record of the outbound
      // transmission reads as "sent to third-party fulfillment".
      return isThirdParty ? 'sent to third-party fulfillment' : 'updated';
    case 'FULFILLMENT_ORDER_RESYNC':
      return 'resynced';
    case 'FULFILLMENT_ORDER_SYNC':
      // The sync is Hardfin reconciling shipment state; it reads as "updated"
      // with its source dropped rather than shown as a "via".
      return 'updated';
    case 'FULFILLMENT_ORDER_FILE_UPDATE':
      return getFileOrLinkChangeVerb(properties, 'FILE', 'file');
    case 'FULFILLMENT_ORDER_URL_LINK_UPDATE':
      return getFileOrLinkChangeVerb(properties, 'URL_LINK', 'link');
    case 'FULFILLMENT_ORDER_SHIPMENT_ADD':
      return 'created a shipment for';
    case 'FULFILLMENT_ORDER_SHIPMENT_REMOVE':
      return 'removed a shipment from';
    case 'FULFILLMENT_ORDER_SHIPMENT_CANCEL':
      return 'cancelled a shipment for';
    case 'FULFILLMENT_ORDER_SHIPMENT_RECOVER':
      return 'recovered a shipment for';
    default:
      return ACTION_LABELS[action];
  }
}

function getPurchaseOrderTriggerLabel(
  eventIdentifier: string,
  action: AuditEventContextAction,
  properties: readonly {
    type: string;
    oldValue?: unknown;
    newValue?: unknown;
  }[],
  isThirdParty: boolean
) {
  switch (eventIdentifier) {
    case 'PURCHASE_ORDER_CREATE':
      return 'created';
    case 'PURCHASE_ORDER_CANCEL':
      return 'cancelled';
    case 'PURCHASE_ORDER_SEND_TO_3PL':
      // The user's send reads as "updated"; the system record of the outbound
      // transmission reads as "sent to third-party fulfillment".
      return isThirdParty ? 'sent to third-party fulfillment' : 'updated';
    case 'PURCHASE_ORDER_RESYNC':
      return 'resynced';
    case 'PURCHASE_ORDER_SYNC':
      // The sync is Hardfin reconciling shipment state; it reads as "updated"
      // with its source dropped rather than shown as a "via".
      return 'updated';
    case 'PURCHASE_ORDER_FILE_UPDATE':
      return getFileOrLinkChangeVerb(properties, 'FILE', 'file');
    case 'PURCHASE_ORDER_URL_LINK_UPDATE':
      return getFileOrLinkChangeVerb(properties, 'URL_LINK', 'link');
    case 'PURCHASE_ORDER_SHIPMENT_ADD':
      return 'created a shipment for';
    case 'PURCHASE_ORDER_SHIPMENT_REMOVE':
      return 'removed a shipment from';
    case 'PURCHASE_ORDER_SHIPMENT_CANCEL':
      return 'cancelled a shipment for';
    case 'PURCHASE_ORDER_SHIPMENT_RECOVER':
      return 'recovered a shipment for';
    default:
      return ACTION_LABELS[action];
  }
}

function getReturnOrderTriggerLabel(
  eventIdentifier: string,
  action: AuditEventContextAction,
  properties: readonly {
    type: string;
    oldValue?: unknown;
    newValue?: unknown;
  }[],
  isThirdParty: boolean
) {
  switch (eventIdentifier) {
    case 'RETURN_ORDER_CREATE':
      return 'created';
    case 'RETURN_ORDER_CANCEL':
      return 'cancelled';
    case 'RETURN_ORDER_SEND_TO_3PL':
      // The user's send reads as "updated"; the system record of the outbound
      // transmission reads as "sent to third-party fulfillment".
      return isThirdParty ? 'sent to third-party fulfillment' : 'updated';
    case 'RETURN_ORDER_RESYNC':
      return 'resynced';
    case 'RETURN_ORDER_SYNC':
      // The sync is Hardfin reconciling shipment state; it reads as "updated"
      // with its source dropped rather than shown as a "via".
      return 'updated';
    case 'RETURN_ORDER_FILE_UPDATE':
      return getFileOrLinkChangeVerb(properties, 'FILE', 'file');
    case 'RETURN_ORDER_URL_LINK_UPDATE':
      return getFileOrLinkChangeVerb(properties, 'URL_LINK', 'link');
    case 'RETURN_ORDER_SHIPMENT_ADD':
      return 'created a shipment for';
    case 'RETURN_ORDER_SHIPMENT_REMOVE':
      return 'removed a shipment from';
    case 'RETURN_ORDER_SHIPMENT_CANCEL':
      return 'cancelled a shipment for';
    case 'RETURN_ORDER_SHIPMENT_RECOVER':
      return 'recovered a shipment for';
    default:
      return ACTION_LABELS[action];
  }
}

const VOWEL_REGEX = /^[aeiou]$/i;

function isVowel(character: string | undefined) {
  if (character === undefined) {
    return false;
  }

  return VOWEL_REGEX.test(character);
}

interface Props {
  /**
   * The audit event context to render.
   */
  context: AuditEventContext;
  /**
   * The entry's state trigger, which names the operation that was performed.
   */
  eventIdentifier: string;
}

/**
 * Renders a source label with a dotted underline and tooltip when the source
 * is a file upload and the source ID (filename) is available.
 */
function SourceLabelText(props: {
  source: NonNullable<AuditEventContext['source']>;
}) {
  if (props.source.type === 'UPLOAD' && props.source.id) {
    return (
      <Tooltip title={props.source.id} arrow>
        <b
          style={{
            textDecorationLine: 'underline',
            textDecorationStyle: 'dotted',
          }}
        >
          {SOURCE_LABELS[props.source.type]}
        </b>
      </Tooltip>
    );
  }

  return <b>{SOURCE_LABELS[props.source.type]}</b>;
}

/**
 * Renders the "via" source indicator if the context has a source
 */
function SourceIndicator(props: { source: AuditEventContext['source'] }) {
  if (!props.source) return null;

  return (
    <Fragment>
      {' '}
      via <SourceLabelText source={props.source} />
    </Fragment>
  );
}

/**
 * A label component that renders a human-readable description of an audit
 * event from a context object.
 */
export function AuditEventLabel(props: Props) {
  if (props.context.type === 'ASSET_EVENT') {
    const action = getActionLabelForAssetEvent(props.context.action);
    const article = isVowel(props.context.name.at(0)) ? 'an' : 'a';
    const eventLabel = `${action} ${article} ${props.context.name.toLowerCase()} event`;

    return (
      <Typography variant="body2">
        <b>{props.context.actor.name}</b> {eventLabel} for{' '}
        <b>asset {props.context.asset.name}</b>
        <SourceIndicator source={props.context.source} />
      </Typography>
    );
  }

  if (props.context.type === 'ASSET_OWNERSHIP_CHANGE') {
    const actionLabels: Partial<Record<AuditEventContextAction, string>> = {
      CREATE: 'changed',
      UPDATE: 'changed',
      DELETE: 'deleted',
    };
    const nounLabel: Partial<Record<AuditEventContextAction, string>> = {
      CREATE: 'ownership',
      UPDATE: 'ownership',
      DELETE: 'sale record',
    };

    return (
      <Typography variant="body2">
        <b>{props.context.actor.name}</b> {actionLabels[props.context.action]}{' '}
        the {nounLabel[props.context.action]} of{' '}
        <b>asset {props.context.asset.name}</b>
        <SourceIndicator source={props.context.source} />
      </Typography>
    );
  }

  if (props.context.type === 'ASSET_LOCATION_CHANGE') {
    return (
      <Typography variant="body2">
        <b>{props.context.actor.name}</b> {ACTION_LABELS[props.context.action]}{' '}
        the location of <b>asset {props.context.asset.name}</b>
        <SourceIndicator source={props.context.source} />
      </Typography>
    );
  }

  if (props.context.type === 'ASSET_METADATA_FIELD') {
    return (
      <Typography variant="body2">
        <b>{props.context.actor.name}</b> deleted an item field that applied to{' '}
        <b>asset {props.context.asset.name}</b>
        <SourceIndicator source={props.context.source} />
      </Typography>
    );
  }

  if (props.context.type === 'ASSET_FILE') {
    const action = getActionLabelForFile(props.context.action);

    return (
      <Typography variant="body2">
        <b>{props.context.actor.name}</b> {action} an attachment for{' '}
        <b>asset {props.context.asset.name}</b>
        <SourceIndicator source={props.context.source} />
      </Typography>
    );
  }

  if (props.context.type === 'ASSET_COST_ADJUSTMENT') {
    return (
      <Typography variant="body2">
        <b>{props.context.actor.name}</b> added a cost adjustment for{' '}
        <b>asset {props.context.asset.name}</b>
        <SourceIndicator source={props.context.source} />
      </Typography>
    );
  }

  if (props.context.type === 'ASSET_USEFUL_LIFE_REVISION') {
    return (
      <Typography variant="body2">
        <b>{props.context.actor.name}</b> added a useful life revision for{' '}
        <b>asset {props.context.asset.name}</b>
        <SourceIndicator source={props.context.source} />
      </Typography>
    );
  }

  if (props.context.type === 'ASSET_URL_LINK') {
    const action = getActionLabelForURLLink(props.context.action);

    return (
      <Typography variant="body2">
        <b>{props.context.actor.name}</b> {action} a link for{' '}
        <b>asset {props.context.asset.name}</b>
        <SourceIndicator source={props.context.source} />
      </Typography>
    );
  }

  if (props.context.type === 'ASSET_PROJECT_UNASSIGNMENT') {
    // Handle assignment unassignments
    // Note: ALL other assignment-related actions are handled below as part of the ASSET_PROJECT_ASSIGNMENT type
    // Note also: there are two ways a user may update the unassignment date. A user can choose to specifically "Unassign"
    // an asset from a project, which will flow here OR user can choose to "update" an existing assignment,
    // which would flow below as part of the ASSET_PROJECT_ASSIGNMENT type with "UPDATE" action
    return (
      <Typography variant="body2">
        <b>{props.context.actor.name}</b> unassigned{' '}
        <b>asset {props.context.asset.name}</b>
        <SourceIndicator source={props.context.source} />
      </Typography>
    );
  }

  if (props.context.type === 'ASSET_PROJECT_ASSIGNMENT') {
    // Handle assignment deletes
    if (props.context.action === 'DELETE') {
      let cascadeText = '';
      if (
        props.context.groupReason &&
        props.context.groupReason === 'CASCADE_PROJECT_DELETE'
      ) {
        // Update text for cascaded project deletes,
        cascadeText = ' automatically by deleting the project';
      }
      return (
        <Typography variant="body2">
          <b>{props.context.actor.name}</b> deleted assignment for{' '}
          <b>asset {props.context.asset.name}</b>
          {cascadeText}
          <SourceIndicator source={props.context.source} />
        </Typography>
      );
    }

    // Handle assignment updates
    // Note: there are two ways a user may update the unassignment date. A user can choose to "update"
    // an existing assignment, which will flow here to this block as part of the ASSET_PROJECT_ASSIGNMENT type and "UPDATE" action
    // OR user can choose to specifically "Unassign" an asset from a project, which would flow above as part of the ASSET_PROJECT_UNASSIGNMENT type
    if (props.context.action === 'UPDATE') {
      return (
        <Typography variant="body2">
          <b>{props.context.actor.name}</b> updated the assignment for{' '}
          <b>asset {props.context.asset.name}</b>
          <SourceIndicator source={props.context.source} />
        </Typography>
      );
    }
    // Handle assignment creation
    return (
      <Typography variant="body2">
        <b>{props.context.actor.name}</b> assigned{' '}
        <b>asset {props.context.asset.name}</b>
        <SourceIndicator source={props.context.source} />
      </Typography>
    );
  }

  if (props.context.type === 'HUBSPOT') {
    return (
      <Typography variant="body2">
        <b>{props.context.actor.name}</b> updated <b>HubSpot configuration</b>
        <SourceIndicator source={props.context.source} />
      </Typography>
    );
  }

  if (props.context.type === 'HUBSPOT_ACTION_SUMMARY') {
    return (
      <Typography variant="body2">
        <b>{props.context.actor.name}</b> synced{' '}
        <b>{props.context.entityType}</b>
        <SourceIndicator source={props.context.source} />
      </Typography>
    );
  }

  if (props.context.type === 'SFDC') {
    return (
      <Typography variant="body2">
        <b>{props.context.actor.name}</b> updated{' '}
        <b>Salesforce configuration</b>
        <SourceIndicator source={props.context.source} />
      </Typography>
    );
  }

  if (props.context.type === 'ORGANIZATION_INTEGRATION') {
    return (
      <Typography variant="body2">
        <b>{props.context.actor.name}</b> updated{' '}
        <b>integration configuration</b>
        <SourceIndicator source={props.context.source} />
      </Typography>
    );
  }

  if (
    props.context.type === 'ASSET' ||
    props.context.type === 'ASSET_UPDATE' ||
    props.context.type === 'ASSET_CREATE'
  ) {
    // A late purchase order that back-dates an existing asset's inventory date/location
    if (props.context.groupReason === 'RETROACTIVE_RECEIVE') {
      return (
        <Typography variant="body2">
          <b>{props.context.actor.name}</b> retroactively received{' '}
          <b>asset {props.context.asset.name}</b>
        </Typography>
      );
    }
    if (props.context.source === undefined || props.context.source === null) {
      return (
        <Typography variant="body2">
          <b>{props.context.actor.name}</b>{' '}
          {ACTION_LABELS[props.context.action]}{' '}
          <b>asset {props.context.asset.name}</b>
        </Typography>
      );
    } else if (props.context.source.type === 'INHERIT') {
      return (
        <Typography variant="body2">
          <b>{props.context.actor.name}</b> inherited defaults for{' '}
          <b>asset {props.context.asset.name}</b> from{' '}
          <b>{props.context.source.name}</b>
        </Typography>
      );
    }
    if (props.context.action === 'UPDATE') {
      return (
        <Typography variant="body2">
          <b>{props.context.actor.name}</b> updated{' '}
          <b>asset {props.context.asset.name}</b> via{' '}
          <SourceLabelText source={props.context.source} />
        </Typography>
      );
    }
    return (
      <Typography variant="body2">
        <b>{props.context.actor.name}</b> imported{' '}
        <b>asset {props.context.asset.name}</b> via{' '}
        <SourceLabelText source={props.context.source} />
      </Typography>
    );
  }

  if (props.context.type === 'ASSET_SCRAP') {
    return (
      <Typography variant="body2">
        <b>{props.context.actor.name}</b> marked{' '}
        <b>asset {props.context.asset.name}</b> as scrapped
        <SourceIndicator source={props.context.source} />
      </Typography>
    );
  }

  if (props.context.type === 'ASSET_UNSCRAP') {
    return (
      <Typography variant="body2">
        <b>{props.context.actor.name}</b> marked{' '}
        <b>asset {props.context.asset.name}</b> as not scrapped
        <SourceIndicator source={props.context.source} />
      </Typography>
    );
  }

  if (
    props.context.type === 'ASSET_TICKET_ASSIGNMENT' ||
    props.context.type === 'ASSET_TICKET_UNASSIGNMENT'
  ) {
    const action =
      props.context.type === 'ASSET_TICKET_ASSIGNMENT'
        ? 'attached'
        : 'unattached';
    return (
      <Typography variant="body2">
        <b>{props.context.actor.name}</b> {action}{' '}
        <b>ticket {props.context.ticketContext.name}</b> to{' '}
        <b>asset {props.context.asset.name}</b>
        <SourceIndicator source={props.context.source} />
      </Typography>
    );
  }

  if (props.context.type === 'PROJECT') {
    return (
      <Typography variant="body2">
        <b>{props.context.actor.name}</b> {ACTION_LABELS[props.context.action]}{' '}
        <b>project {props.context.project.name}</b>
        <SourceIndicator source={props.context.source} />
      </Typography>
    );
  }

  if (props.context.type === 'PROJECT_FILE') {
    const action = getActionLabelForFile(props.context.action);

    return (
      <Typography variant="body2">
        <b>{props.context.actor.name}</b> {action} an attachment for{' '}
        <b>project {props.context.project.name}</b>
        <SourceIndicator source={props.context.source} />
      </Typography>
    );
  }

  if (props.context.type === 'PROJECT_URL_LINK') {
    const action = getActionLabelForURLLink(props.context.action);

    return (
      <Typography variant="body2">
        <b>{props.context.actor.name}</b> {action} a link for{' '}
        <b>project {props.context.project.name}</b>
        <SourceIndicator source={props.context.source} />
      </Typography>
    );
  }

  if (props.context.type === 'PROJECT_COST') {
    return (
      <Typography variant="body2">
        <b>{props.context.actor.name}</b> {ACTION_LABELS[props.context.action]}{' '}
        a project cost for <b>project {props.context.project.name}</b>
        <SourceIndicator source={props.context.source} />
      </Typography>
    );
  }
  if (props.context.type === 'CUSTOMER_MERGE') {
    return (
      <Typography variant="body2">
        <b>{props.context.actor.name}</b> {ACTION_LABELS[props.context.action]}{' '}
        <b>{props.context.removed.name}</b> into{' '}
        <b>{props.context.retained.name}</b>
        <SourceIndicator source={props.context.source} />
      </Typography>
    );
  }
  if (props.context.type === 'CONTACT_MERGE') {
    return (
      <Typography variant="body2">
        <b>{props.context.actor.name}</b> {ACTION_LABELS[props.context.action]}{' '}
        <b>{props.context.removed.name}</b> into{' '}
        <b>{props.context.retained.name}</b>
        <SourceIndicator source={props.context.source} />
      </Typography>
    );
  }
  if (props.context.type === 'LOCATION_MERGE') {
    return (
      <Typography variant="body2">
        <b>{props.context.actor.name}</b> {ACTION_LABELS[props.context.action]}{' '}
        <b>{props.context.removed.name}</b> into{' '}
        <b>{props.context.retained.name}</b>
        <SourceIndicator source={props.context.source} />
      </Typography>
    );
  }

  if (props.context.type === 'ITEM_MERGE') {
    const mergedItemProperty = props.context.properties.find(
      (prop) => prop.type === 'CACHED_ITEM_MERGE'
    );
    const removedItemName =
      mergedItemProperty?.newValue?.name || 'Unknown item';

    return (
      <Typography variant="body2">
        <b>{props.context.actor.name}</b> {ACTION_LABELS[props.context.action]}{' '}
        <b>{removedItemName}</b> into <b>{props.context.item.name}</b>
        <SourceIndicator source={props.context.source} />
      </Typography>
    );
  }

  if (
    props.context.type === 'PROJECT_NOTE' ||
    props.context.type === 'TICKET_NOTE'
  ) {
    const NOTE_ACTION_LABELS: Record<AuditEventContextAction, string> = {
      ...ACTION_LABELS,
      CREATE: 'added',
      DELETE: 'removed',
    };

    const NOTE_PREPOSITIONS: Partial<Record<AuditEventContextAction, string>> =
      {
        CREATE: 'to',
        DELETE: 'from',
        UPDATE: 'for',
        LOGIN: 'to', // This should never happen but is included for type safety
      };
    let to: ReactNode;
    if (props.context.type === 'TICKET_NOTE') {
      to = <b>ticket {props.context.ticket.name}</b>;
    }
    if (props.context.type === 'PROJECT_NOTE') {
      to = <b>project {props.context.project.name}</b>;
    }
    return (
      <Typography variant="body2">
        <b>{props.context.actor.name}</b>{' '}
        {NOTE_ACTION_LABELS[props.context.action]} a note{' '}
        {NOTE_PREPOSITIONS[props.context.action]} {to}
        <SourceIndicator source={props.context.source} />
      </Typography>
    );
  }

  if (
    props.context.type === 'PROJECT_TICKET_ASSIGNMENT' ||
    props.context.type === 'PROJECT_TICKET_UNASSIGNMENT'
  ) {
    const action =
      props.context.type === 'PROJECT_TICKET_ASSIGNMENT'
        ? 'attached'
        : 'unattached';
    return (
      <Typography variant="body2">
        <b>{props.context.actor.name}</b> {action}{' '}
        <b>ticket {props.context.ticketContext.name}</b> to{' '}
        <b>project {props.context.project.name}</b>
        <SourceIndicator source={props.context.source} />
      </Typography>
    );
  }
  if (props.context.type === 'PROJECT_LINE_ITEM_CHARGE_SCHEDULE') {
    const itemName = props.context.lineItemContext?.item.name;
    return (
      <Typography variant="body2">
        <b>{props.context.actor.name}</b> {ACTION_LABELS[props.context.action]}{' '}
        the charge schedule
        {itemName ? (
          <>
            {' '}
            for <b>{itemName}</b>
          </>
        ) : null}{' '}
        on <b>project {props.context.project.name}</b>
        <SourceIndicator source={props.context.source} />
      </Typography>
    );
  }

  if (props.context.type === 'LOCATION') {
    if (props.context.source === undefined || props.context.source === null) {
      return (
        <Typography variant="body2">
          <b>{props.context.actor.name}</b>{' '}
          {ACTION_LABELS[props.context.action]}{' '}
          <b>location {getEffectiveLocationName(props.context.location)}</b>
        </Typography>
      );
    }

    return (
      <Typography variant="body2">
        <b>{props.context.actor.name}</b> imported{' '}
        <b>location {props.context.location.name}</b> via{' '}
        <SourceLabelText source={props.context.source} />
      </Typography>
    );
  }

  if (props.context.type === 'CUSTOMER') {
    if (props.context.source === undefined || props.context.source === null) {
      return (
        <Typography variant="body2">
          <b>{props.context.actor.name}</b>{' '}
          {ACTION_LABELS[props.context.action]}{' '}
          <b>customer {props.context.customer.name}</b>
        </Typography>
      );
    }

    return (
      <Typography variant="body2">
        <b>{props.context.actor.name}</b> imported{' '}
        <b>customer {props.context.customer.name}</b> via{' '}
        <SourceLabelText source={props.context.source} />
      </Typography>
    );
  }

  if (props.context.type === 'CONTACT') {
    return (
      <Typography variant="body2">
        <b>{props.context.actor.name}</b> {ACTION_LABELS[props.context.action]}{' '}
        <b>contact {props.context.contact.name}</b>
        <SourceIndicator source={props.context.source} />
      </Typography>
    );
  }

  if (props.context.type === 'COST') {
    return (
      <Typography variant="body2">
        <b>{props.context.actor.name}</b> {ACTION_LABELS[props.context.action]}{' '}
        a <b>cost {props.context.cost.name}</b>
        <SourceIndicator source={props.context.source} />
      </Typography>
    );
  }

  if (
    props.context.type === 'FULFILLMENT_ORDER' ||
    props.context.type === 'FULFILLMENT_ORDER_3PL'
  ) {
    const triggerLabel = getFulfillmentOrderTriggerLabel(
      props.eventIdentifier,
      props.context.action,
      props.context.properties,
      props.context.type === 'FULFILLMENT_ORDER_3PL'
    );
    return (
      <Typography variant="body2">
        <b>{props.context.actor.name}</b>
        {triggerLabel ? ` ${triggerLabel} ` : ' '}
        <b>
          fulfillment order{' '}
          {props.context.fulfillmentOrder.name ??
            props.context.fulfillmentOrder.key}
        </b>
        <SourceIndicator source={props.context.source} />
      </Typography>
    );
  }

  if (
    props.context.type === 'RETURN_ORDER' ||
    props.context.type === 'RETURN_ORDER_3PL'
  ) {
    const triggerLabel = getReturnOrderTriggerLabel(
      props.eventIdentifier,
      props.context.action,
      props.context.properties,
      props.context.type === 'RETURN_ORDER_3PL'
    );
    return (
      <Typography variant="body2">
        <b>{props.context.actor.name}</b>
        {triggerLabel ? ` ${triggerLabel} ` : ' '}
        <b>
          return order{' '}
          {props.context.returnOrder.name ?? props.context.returnOrder.key}
        </b>
        <SourceIndicator source={props.context.source} />
      </Typography>
    );
  }

  if (
    props.context.type === 'PURCHASE_ORDER' ||
    props.context.type === 'PURCHASE_ORDER_3PL'
  ) {
    const triggerLabel = getPurchaseOrderTriggerLabel(
      props.eventIdentifier,
      props.context.action,
      props.context.properties,
      props.context.type === 'PURCHASE_ORDER_3PL'
    );
    return (
      <Typography variant="body2">
        <b>{props.context.actor.name}</b>
        {triggerLabel ? ` ${triggerLabel} ` : ' '}
        <b>
          purchase order{' '}
          {props.context.purchaseOrder.name ?? props.context.purchaseOrder.key}
        </b>
        <SourceIndicator source={props.context.source} />
      </Typography>
    );
  }

  if (props.context.type === 'SHIPMENT') {
    return (
      <Typography variant="body2">
        <b>{props.context.actor.name}</b>{' '}
        {getShipmentOperationLabel(
          props.context.operation,
          props.context.action
        )}{' '}
        <b>
          shipment {props.context.shipment.name ?? props.context.shipment.key}
        </b>
        <SourceIndicator source={props.context.source} />
      </Typography>
    );
  }

  if (props.context.type === 'TIME_ACTIVITY') {
    return (
      <Typography variant="body2">
        <b>{props.context.actor.name}</b> {ACTION_LABELS[props.context.action]}{' '}
        a <b>time record</b>
        <SourceIndicator source={props.context.source} />
      </Typography>
    );
  }

  if (props.context.type === 'ITEM_PRICING') {
    return (
      <Typography variant="body2">
        <b>{props.context.actor.name}</b> {ACTION_LABELS[props.context.action]}{' '}
        default pricing for <b>item {props.context.item.name}</b>
        <SourceIndicator source={props.context.source} />
      </Typography>
    );
  }

  if (props.context.type === 'ITEM') {
    if (props.context.source === undefined || props.context.source === null) {
      return (
        <Typography variant="body2">
          <b>{props.context.actor.name}</b>{' '}
          {ACTION_LABELS[props.context.action]}{' '}
          <b>item {props.context.item.name}</b>
        </Typography>
      );
    }

    return (
      <Typography variant="body2">
        <b>{props.context.actor.name}</b> imported{' '}
        <b>item {props.context.item.name}</b> via{' '}
        <SourceLabelText source={props.context.source} />
      </Typography>
    );
  }

  if (props.context.type === 'BUNDLE') {
    return (
      <Typography variant="body2">
        <b>{props.context.actor.name}</b> {ACTION_LABELS[props.context.action]}{' '}
        <b>bundle {props.context.bundle.name}</b>
        <SourceIndicator source={props.context.source} />
      </Typography>
    );
  }

  if (props.context.type === 'ITEM_FIELD') {
    return (
      <Typography variant="body2">
        <b>{props.context.actor.name}</b> {ACTION_LABELS[props.context.action]}{' '}
        <b>{props.context.field.name}</b> for{' '}
        <b>item {props.context.item.name}</b>
        <SourceIndicator source={props.context.source} />
      </Typography>
    );
  }

  if (props.context.type === 'ORGANIZATION') {
    return (
      <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
        <b>{props.context.actor.name}</b> {ACTION_LABELS[props.context.action]}{' '}
        <b>organization {props.context.organization.name}</b>
        <SourceIndicator source={props.context.source} />
      </Typography>
    );
  }

  if (props.context.type === 'ORGANIZATION_MEMBER') {
    if (props.context.action === 'CREATE') {
      return (
        <Typography variant="body2">
          <b>{props.context.actor.name}</b> added a member to{' '}
          <b>organization {props.context.organization.name}</b>
          <SourceIndicator source={props.context.source} />
        </Typography>
      );
    }

    if (props.context.action === 'UPDATE') {
      const memberText = props.context.member?.name
        ? `${props.context.member.name}`
        : 'a member';
      return (
        <Typography variant="body2">
          <b>{props.context.actor.name}</b> updated membership for{' '}
          <b>{memberText}</b> in{' '}
          <b>organization {props.context.organization.name}</b>
          <SourceIndicator source={props.context.source} />
        </Typography>
      );
    }

    return (
      <Typography variant="body2">
        <b>{props.context.actor.name}</b> {ACTION_LABELS[props.context.action]}{' '}
        <b>organization {props.context.organization.name}</b>
        <SourceIndicator source={props.context.source} />
      </Typography>
    );
  }

  if (props.context.type === 'ORGANIZATION_EXTERNAL_USER') {
    if (props.context.action === 'CREATE') {
      return (
        <Typography variant="body2">
          <b>{props.context.actor.name}</b> added{' '}
          <b>custom notification {props.context.externalUser.name}</b> to{' '}
          <b>organization {props.context.organization.name}</b>
          <SourceIndicator source={props.context.source} />
        </Typography>
      );
    }

    if (props.context.action === 'UPDATE') {
      return (
        <Typography variant="body2">
          <b>{props.context.actor.name}</b> updated{' '}
          <b>custom notification {props.context.externalUser.name}</b> in{' '}
          <b>organization {props.context.organization.name}</b>
          <SourceIndicator source={props.context.source} />
        </Typography>
      );
    }

    if (props.context.action === 'DELETE') {
      return (
        <Typography variant="body2">
          <b>{props.context.actor.name}</b> removed{' '}
          <b>custom notification {props.context.externalUser.name}</b> from{' '}
          <b>organization {props.context.organization.name}</b>
          <SourceIndicator source={props.context.source} />
        </Typography>
      );
    }

    return (
      <Typography variant="body2">
        <b>{props.context.actor.name}</b> {ACTION_LABELS[props.context.action]}{' '}
        <b>custom notification {props.context.externalUser.name}</b>
        <SourceIndicator source={props.context.source} />
      </Typography>
    );
  }

  if (props.context.type === 'ORGANIZATION_PROJECT_WORKFLOW') {
    if (props.context.action === 'CREATE') {
      return (
        <Typography variant="body2">
          <b>{props.context.actor.name}</b> created{' '}
          <b>project workflow {props.context.workflow.name}</b>
          <SourceIndicator source={props.context.source} />
        </Typography>
      );
    }

    if (props.context.action === 'UPDATE') {
      return (
        <Typography variant="body2">
          <b>{props.context.actor.name}</b> updated{' '}
          <b>project workflow {props.context.workflow.name}</b>
          <SourceIndicator source={props.context.source} />
        </Typography>
      );
    }

    if (props.context.action === 'DELETE') {
      return (
        <Typography variant="body2">
          <b>{props.context.actor.name}</b> deleted{' '}
          <b>project workflow {props.context.workflow.name}</b> from
          <SourceIndicator source={props.context.source} />
        </Typography>
      );
    }

    return (
      <Typography variant="body2">
        <b>{props.context.actor.name}</b> {ACTION_LABELS[props.context.action]}{' '}
        <b>project workflow {props.context.workflow.name}</b>
        <SourceIndicator source={props.context.source} />
      </Typography>
    );
  }

  if (props.context.type === 'PROJECT_WORKFLOW_CASCADE') {
    if (props.context.action === 'UPDATE') {
      return (
        <Typography variant="body2">
          <b>{props.context.actor.name}</b> updated project workflow{' '}
          <b>{props.context.oldWorkflow.name}</b>
          <SourceIndicator source={props.context.source} />
        </Typography>
      );
    }

    return (
      <Typography variant="body2">
        <b>{props.context.actor.name}</b> updated project workflow{' '}
        <b>project {props.context.project.name}</b>
        <SourceIndicator source={props.context.source} />
      </Typography>
    );
  }

  if (props.context.type === 'PROJECT_COST_CASCADE') {
    const label =
      props.context.action === 'UPDATE'
        ? 'updated'
        : props.context.action === 'CREATE'
          ? 'attached'
          : 'detached';
    return (
      <Typography variant="body2">
        <b>{props.context.actor.name}</b> {label} a project cost for{' '}
        <b>project {props.context.project.name}</b>
        <SourceIndicator source={props.context.source} />
      </Typography>
    );
  }

  if (props.context.type === 'PROJECT_CHARGE_CASCADE') {
    const verb = props.context.action === 'UPDATE' ? 'updated' : 'cancelled';
    const chargeText = props.context.charge
      ? `charge ${props.context.charge.label}`
      : 'charges';
    return (
      <Typography variant="body2">
        <b>{props.context.actor.name}</b> {verb} {chargeText} on{' '}
        <b>project {props.context.project.name}</b>
        <SourceIndicator source={props.context.source} />
      </Typography>
    );
  }

  if (props.context.type === 'PROJECT_TIME_ACTIVITY_CASCADE') {
    const label =
      props.context.action === 'UPDATE'
        ? 'updated'
        : props.context.action === 'CREATE'
          ? 'attached'
          : 'detached';
    return (
      <Typography variant="body2">
        <b>{props.context.actor.name}</b> {label} a time record for{' '}
        <b>project {props.context.project.name}</b>
        <SourceIndicator source={props.context.source} />
      </Typography>
    );
  }

  if (props.context.type === 'PERSON_NOTIFICATION') {
    return (
      <Typography variant="body2">
        <b>{props.context.actor.name}</b> changed notification settings in{' '}
        <b>{props.context.organization.name}</b>
        <SourceIndicator source={props.context.source} />
      </Typography>
    );
  }

  if (props.context.type === 'PERSON_CONNECTED_APPLICATION') {
    const label = toConnectedApplicationVerb(props.context.action);
    return (
      <Typography variant="body2">
        <b>{props.context.actor.name}</b> {label}{' '}
        <b>{props.context.application.name}</b> in{' '}
        <b>{props.context.organization.name}</b>
        <SourceIndicator source={props.context.source} />
      </Typography>
    );
  }

  if (props.context.type === 'PERSON') {
    // Signing in or out is the person's own act, so the entry names nobody but the actor
    if (props.context.action === 'LOGIN' || props.context.action === 'LOGOUT') {
      return (
        <Typography variant="body2">
          <b>{props.context.actor.name}</b>{' '}
          {ACTION_LABELS[props.context.action]}
          <SourceIndicator source={props.context.source} />
        </Typography>
      );
    }

    return (
      <Typography variant="body2">
        <b>{props.context.actor.name}</b> {ACTION_LABELS[props.context.action]}{' '}
        <b>person {props.context.person.name}</b>
        <SourceIndicator source={props.context.source} />
      </Typography>
    );
  }

  if (props.context.type === 'PROJECT_DELETE') {
    return (
      <Typography variant="body2">
        <b>{props.context.actor.name}</b> {ACTION_LABELS[props.context.action]}{' '}
        <b>project {props.context.projectContext.name}</b>
        <SourceIndicator source={props.context.source} />
      </Typography>
    );
  }

  if (props.context.type === 'NOTE') {
    return (
      <Typography variant="body2">
        <b>{props.context.actor.name}</b> {ACTION_LABELS[props.context.action]}{' '}
        a <b>note</b>
        <SourceIndicator source={props.context.source} />
      </Typography>
    );
  }

  if (props.context.type === 'CHARGE') {
    return (
      <Typography variant="body2">
        <b>{props.context.actor.name}</b> {ACTION_LABELS[props.context.action]}{' '}
        a <b>charge</b>
        <SourceIndicator source={props.context.source} />
      </Typography>
    );
  }

  if (props.context.type === 'CHARGE_SPLIT') {
    return (
      <Typography variant="body2">
        <b>{props.context.actor.name}</b> split{' '}
        <b>{props.context.charge.name}</b> from{' '}
        <b>{props.context.originalCharge.name}</b>
        <SourceIndicator source={props.context.source} />
      </Typography>
    );
  }

  if (
    props.context.type === 'TICKET_CREATE' ||
    props.context.type === 'TICKET_UPDATE'
  ) {
    const action =
      props.context.type === 'TICKET_CREATE' ? 'created' : 'updated';
    return (
      <Typography variant="body2">
        <b>{props.context.actor.name}</b> {action}{' '}
        <b>ticket {props.context.ticket.name}</b>
        <SourceIndicator source={props.context.source} />
      </Typography>
    );
  }

  if (
    props.context.type === 'TICKET_FILE_CREATE' ||
    props.context.type === 'TICKET_FILE_DELETE'
  ) {
    return (
      <Typography variant="body2">
        <b>{props.context.actor.name}</b> {ACTION_LABELS[props.context.action]}{' '}
        an attachment for <b>ticket {props.context.ticket.name}</b>
        <SourceIndicator source={props.context.source} />
      </Typography>
    );
  }

  if (
    props.context.type === 'TICKET_NOTE_UPDATE' ||
    props.context.type === 'TICKET_NOTE_BIND' ||
    props.context.type === 'TICKET_NOTE_UNBIND'
  ) {
    return (
      <Typography variant="body2">
        <b>{props.context.actor.name}</b> {ACTION_LABELS[props.context.action]}{' '}
        a <b>ticket note</b>
        <SourceIndicator source={props.context.source} />
      </Typography>
    );
  }

  if (props.context.type === 'ORGANIZATION_ACCOUNTING') {
    return (
      <Typography variant="body2">
        <b>{props.context.actor.name}</b> {ACTION_LABELS[props.context.action]}{' '}
        <b>accounting configurations</b> for{' '}
        <b>organization {props.context.organization.name}</b>
        <SourceIndicator source={props.context.source} />
      </Typography>
    );
  }

  if (props.context.type === 'ORGANIZATION_ATTENTION_THRESHOLDS') {
    return (
      <Typography variant="body2">
        <b>{props.context.actor.name}</b> {ACTION_LABELS[props.context.action]}{' '}
        <b>attention thresholds</b> for{' '}
        <b>organization {props.context.organization.name}</b>
        <SourceIndicator source={props.context.source} />
      </Typography>
    );
  }

  if (
    props.context.type === 'PROJECT_ASSET' ||
    props.context.type === 'PROJECT_ASSET_UNASSIGNMENT'
  ) {
    return (
      <Typography variant="body2">
        <b>{props.context.actor.name}</b> {ACTION_LABELS[props.context.action]}{' '}
        an asset assignment for <b>project {props.context.project.name}</b>
        <SourceIndicator source={props.context.source} />
      </Typography>
    );
  }

  if (props.context.type === 'PERSON_ORGANIZATION_MEMBERSHIP') {
    return (
      <Typography variant="body2">
        <b>{props.context.actor.name}</b> {ACTION_LABELS[props.context.action]}{' '}
        organization membership
        {props.context.member ? (
          <>
            {' '}
            for <b>{props.context.member.name}</b>
          </>
        ) : null}
        <SourceIndicator source={props.context.source} />
      </Typography>
    );
  }

  if (props.context.type === 'DATA_EXPORT') {
    return (
      <Typography variant="body2">
        <b>{props.context.actor.name}</b> exported{' '}
        <b>{props.context.entityType}</b> data
        <SourceIndicator source={props.context.source} />
      </Typography>
    );
  }

  if (props.context.type === 'EXTERNAL_LINK') {
    const integrationLabel =
      SOURCE_NOUN_LABELS[props.context.integrationSource as AuditSourceType] ??
      props.context.integrationSource;
    const entityTypeLabel = props.context.entityType.toLowerCase();

    return (
      <Typography variant="body2">
        <b>{props.context.actor.name}</b>{' '}
        {props.context.action === 'CREATE' ? 'linked' : 'updated link for'}{' '}
        <b>
          {entityTypeLabel} {props.context.entity.name}
        </b>{' '}
        to <b>{integrationLabel}</b>
        <SourceIndicator source={props.context.source} />
      </Typography>
    );
  }

  // Project-to-project reference events track changes to a project's "previous project" field.
  // CREATE = setting a previous project, UPDATE = changing it, DELETE = clearing it
  if (props.context.type === 'PROJECT_PROJECT_REFERENCE') {
    if (props.context.action === 'CREATE') {
      return (
        <Typography variant="body2">
          <b>{props.context.actor.name}</b> set the previous project to{' '}
          <b>{props.context.referencedProject?.name}</b> for project{' '}
          <b>{props.context.invokingProject.name}</b>
        </Typography>
      );
    }

    if (props.context.action === 'UPDATE') {
      return (
        <Typography variant="body2">
          <b>{props.context.actor.name}</b> updated the previous project from{' '}
          <b>{props.context.oldEntity?.name}</b> to{' '}
          <b>{props.context.referencedProject?.name}</b> for project{' '}
          <b>{props.context.invokingProject.name}</b>
        </Typography>
      );
    }

    // DELETE
    return (
      <Typography variant="body2">
        <b>{props.context.actor.name}</b> cleared the previous project for
        project <b>{props.context.invokingProject.name}</b>
      </Typography>
    );
  }

  if (props.context.type === 'LOCATION_ZONE') {
    return (
      <Typography variant="body2">
        <b>{props.context.actor.name}</b> {ACTION_LABELS[props.context.action]}{' '}
        zone <b>{props.context.zone.name}</b>
        <SourceIndicator source={props.context.source} />
      </Typography>
    );
  }

  if (props.context.type === 'PROJECT_BILLING') {
    return (
      <Typography variant="body2">
        <b>{props.context.actor.name}</b> {ACTION_LABELS[props.context.action]}{' '}
        billing periods on <b>project {props.context.project.name}</b>
        <SourceIndicator source={props.context.source} />
      </Typography>
    );
  }

  if (props.context.type === 'INVOICE') {
    return (
      <Typography variant="body2">
        <b>{props.context.actor.name}</b> {ACTION_LABELS[props.context.action]}{' '}
        invoice <b>{props.context.invoice.invoiceNumber}</b>
        <SourceIndicator source={props.context.source} />
      </Typography>
    );
  }

  if (props.context.type === 'INVOICE_PAYMENT_CASCADE') {
    return (
      <Typography variant="body2">
        <b>{props.context.actor.name}</b> applied payment to invoice{' '}
        <b>{props.context.invoice.invoiceNumber}</b>
        <SourceIndicator source={props.context.source} />
      </Typography>
    );
  }

  if (props.context.type === 'PROJECT_INVOICE_CASCADE') {
    const verb = props.context.action === 'CREATE' ? 'created' : 'updated';
    return (
      <Typography variant="body2">
        <b>{props.context.actor.name}</b> {verb}{' '}
        <b>{props.context.invoice.invoiceNumber}</b> for project{' '}
        <b>{props.context.project.name}</b>
        <SourceIndicator source={props.context.source} />
      </Typography>
    );
  }

  props.context.type satisfies never;

  throw new Error('Invalid audit event context', { cause: props.context });
}

/** Says what a person did to a connected application or one of its sessions. */
function toConnectedApplicationVerb(action: AuditEventContextAction) {
  switch (action) {
    case 'CREATE':
      return 'connected';
    case 'DELETE':
      return 'disconnected';
    case 'LOGIN':
      return 'signed in with';
    case 'LOGOUT':
      return 'signed out of';
    default:
      return 'changed the permissions of';
  }
}
